/**
 * ProductionWorkflowPanel — the 'workflow' Console area (see `areas.ts`).
 *
 * Floor rules and station behaviour: OOS handling / bin confirmation / batch
 * cap, station toggles, picking, packing stations, bin creation, hotkeys,
 * and the per-user Issues-tab visibility preference. Renders in the same
 * order as the `areas.ts` 'workflow' section list.
 */
import {
  GitBranch,
  PackageCheck,
  Package,
  Keyboard,
  SlidersHorizontal,
  Clock,
  PackageX,
  Layers,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLocation } from '@/contexts/LocationContext';
import { WorkflowSettingsSection } from '../components/WorkflowSettingsSection';
import { PackingStationsSection } from '../components/PackingStationsSection';
import { BinCreationSection } from '../components/BinCreationSection';
import { HotkeySettingsSection } from '../components/HotkeySettingsSection';
import { IssueTabsSettingsSection } from '../components/IssueTabsSettingsSection';
import { OrderHoldSettingsSection } from '../components/OrderHoldSettingsSection';
import { StarshipitLabelPrefetchSection } from '../components/StarshipitLabelPrefetchSection';
import { PreprintSettingsSection } from '../components/PreprintSettingsSection';
import { PortlessBatchGateSection } from '../components/PortlessBatchGateSection';
import { StationSettingsSection } from '../sections/StationSettingsSection';
import { PickingSettingsSection } from '../sections/PickingSettingsSection';
import { PicklistOrderSection } from '../sections/PicklistOrderSection';
import { SettingsSection } from '../components/console/SettingsSection';
import { useProductionSettings } from '../hooks/useProductionSettings';
import { toggleHiddenIssueTab } from '@/features/production-issues/issueTabs';
import type { TabValue } from '@/features/production-issues/types';

/**
 * Show/hide the Production Issues tabs — a per-user (localStorage) view
 * preference. Applied directly on toggle (no dirty-model save step), same as
 * the old god component's `handleSettingChange` wiring.
 */
function IssueTabsPanelSection() {
  const { settings, updateSetting } = useProductionSettings();

  const handleToggleTab = (tab: TabValue, visible: boolean) => {
    updateSetting('hiddenIssueTabs', toggleHiddenIssueTab(settings.hiddenIssueTabs, tab, visible));
    toast.success(visible ? 'Tab shown' : 'Tab hidden');
  };

  return (
    <SettingsSection
      id="issue-tabs"
      title="Issue tabs"
      description="Show or hide tabs on the Production Issues page."
      icon={SlidersHorizontal}
      scope="browser"
    >
      <IssueTabsSettingsSection hiddenTabs={settings.hiddenIssueTabs} onToggleTab={handleToggleTab} />
    </SettingsSection>
  );
}

export function ProductionWorkflowPanel() {
  const { selectedLocation } = useLocation();

  return (
    <>
      <SettingsSection
        id="workflow-rules"
        title="Workflow rules"
        description="Out-of-stock handling, bin confirmation, and the manual batch cap."
        icon={GitBranch}
        scope="org"
      >
        <WorkflowSettingsSection embedded />
      </SettingsSection>

      <StationSettingsSection />

      <PickingSettingsSection />

      <PicklistOrderSection />

      <SettingsSection
        id="order-hold"
        title="Order editing hold"
        description="Hold new orders ~2h before batching so customers can still edit in Shopify."
        icon={Clock}
        scope="location"
      >
        <OrderHoldSettingsSection embedded locationId={selectedLocation?.id} />
      </SettingsSection>

      <SettingsSection
        id="starshipit-label-prefetch"
        title="Pre-made shipping label"
        description="Buy the StarShipIt label when the order is scanned at packing, so Print is instant."
        icon={Zap}
        scope="location"
      >
        <StarshipitLabelPrefetchSection embedded locationId={selectedLocation?.id} />
      </SettingsSection>

      <SettingsSection
        id="preprint-permission"
        title="Pre-printing"
        description="Which locations may pre-print garments, add pre-printed stock, or print stock films."
        icon={Layers}
        scope="location"
      >
        <PreprintSettingsSection embedded locationId={selectedLocation?.id} />
      </SettingsSection>

      <SettingsSection
        id="portless-batch-gate"
        title="Portless label gate"
        description="Hold orders out of Ready to Batch until Portless has issued a shipping label."
        icon={PackageX}
        scope="location"
      >
        <PortlessBatchGateSection embedded locationId={selectedLocation?.id} />
      </SettingsSection>

      <SettingsSection
        id="packing-stations"
        title="Packing stations"
        description="Packing station instances and routing rules."
        icon={PackageCheck}
        scope="org"
      >
        <PackingStationsSection />
      </SettingsSection>

      <SettingsSection
        id="bins"
        title="Bin management"
        description="Create and manage production bins for the selected location."
        icon={Package}
        scope="location"
      >
        <BinCreationSection />
      </SettingsSection>

      <SettingsSection
        id="hotkeys"
        title="Hotkeys"
        description="Keyboard shortcuts for production station actions."
        icon={Keyboard}
        scope="org"
      >
        <HotkeySettingsSection embedded />
      </SettingsSection>

      <IssueTabsPanelSection />
    </>
  );
}
